const LINK_TYPES = {
  internal: 'internal_page',
  dropdown: 'dropdown_only',
};

const MAX_DROPDOWN_ITEMS = 6;

const INTERNAL_PAGES = [
  { page: 'meet-the-characters', text: 'Meet the Characters' },
  { page: 'explore-porthole', text: 'Explore Porthole' },
  { page: 'about', text: 'About the Show' },
  { page: 'vision-and-values', text: 'Vision and Values' },
  { page: 'activities', text: 'Activities' },
  { page: 'watch', text: 'Watch' },
  { page: 'gallery', text: 'Gallery' },
  { page: 'games', text: 'Games' },
];

const normaliseKey = (value = '') => {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
};

const getDropdownKey = ({ text = '', page = '' }) => {
  return normaliseKey(page) || normaliseKey(text);
};

const joinUrlParts = (parent = '', child = '') => {
  const parentPath = parent.replace(/^\/+|\/+$/g, '');
  const childPath = child.replace(/^\/+|\/+$/g, '');

  return [parentPath, childPath].filter(Boolean).join('/');
};

const buildInternalLinkIndex = (subpages = {}) => {
  const index = new Map();
  const dynamicLinks = [
    ['articles', ''],
    ['meet_the_characters', 'meet-the-characters'],
    ['explore_porthole', 'explore-porthole'],
    ['games', 'games'],
  ].flatMap(([key, parent]) => {
    const pages = Array.isArray(subpages[key]) ? subpages[key] : [];

    return pages
      .filter((page) => typeof page?.friendly_url === 'string' && page.friendly_url !== '')
      .map((page) => ({
        text: page.name,
        page: joinUrlParts(parent, page.friendly_url),
        friendly_url: page.friendly_url,
      }));
  });

  const addRoute = (route, link) => {
    const path = joinUrlParts('', route);
    index.set(path, link);
    index.set(`/${path}`, link);
  };

  [...dynamicLinks, ...INTERNAL_PAGES].forEach((link) => addRoute(link.page, link));

  // Exact routes take precedence over legacy slugs; aliases follow content-type order.
  dynamicLinks.forEach((link) => {
    const slug = joinUrlParts('', link.friendly_url);
    if (!index.has(slug)) {
      addRoute(slug, link);
    }
  });

  return index;
};

const resolveInternalLink = (page, index) => {
  return typeof page === 'string' ? index.get(joinUrlParts('', page)) : undefined;
};

const parseLink = (link = {}) => {
  const {
    text = '',
    link_type = '',
    external_url = '',
    external_url_target = '',
    page = '',
    dropdown_items = [],
  } = link || {};

  return {
    text,
    link_type,
    external_url,
    external_url_target,
    page,
    dropdown_items,
  };
};

const parseDropdownItems = (dropdownItems = [], parentPage = '', internalLinks, dropdownOnly = false) => {
  if (!Array.isArray(dropdownItems) || dropdownItems?.length === 0) {
    return [];
  }

  const validItems = dropdownItems.filter((url) => typeof url === 'string' && url !== '');

  const visibleItems = dropdownOnly ? validItems : validItems.slice(0, MAX_DROPDOWN_ITEMS);
  const children = visibleItems.flatMap((url) => {
    const subpage = resolveInternalLink(url, internalLinks);

    if (!subpage) {
      return [];
    }

    return {
      text: subpage.text,
      link_type: LINK_TYPES.internal,
      external_url: '',
      external_url_target: '',
      page: subpage.page,
    };
  });

  if (!dropdownOnly && validItems.length > MAX_DROPDOWN_ITEMS && parentPage !== '') {
    children.push({
      text: 'More',
      link_type: LINK_TYPES.internal,
      external_url: '',
      external_url_target: '',
      page: parentPage,
      extra_classname: 'more-link',
    });
  }

  return children;
};

export const parseNavigation = ({ data = {}, subpages = {} }) => {
  const { content = [] } = data;
  const internalLinks = buildInternalLinkIndex(subpages);

  const links = [];

  content.forEach((link) => {
    const parsedLink = parseLink(link?.link);
    const { text = '', link_type = '', external_url = '', page = '', dropdown_items = [] } = parsedLink;

    if (link_type == 'external_url' && external_url === '') {
      return;
    }

    const dropdownOnly = link_type === LINK_TYPES.dropdown;
    const resolvedPage = dropdownOnly
      ? ''
      : link_type === LINK_TYPES.internal
      ? resolveInternalLink(page, internalLinks)?.page || page
      : page;
    const dropdownKey = getDropdownKey({ text, page: resolvedPage });
    const children = parseDropdownItems(dropdown_items, resolvedPage, internalLinks, dropdownOnly);

    if (dropdownOnly && children.length === 0) {
      return;
    }

    links.push({
      ...parsedLink,
      page: resolvedPage,
      dropdown_key: dropdownKey,
      children,
    });
  });

  // JW_WATCH &&
  //   links.push({
  //     text: 'Vision & Values',
  //     link_type: 'internal_page',
  //     external_url: '',
  //     external_url_target: '',
  //     page: 'vision-and-values',
  //   });

  return links;
};
